import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNotification from 'hooks/useNotification';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';

const TemplateTaskEditModal = ({
  show,
  handleClose,
  data,
  agents = [],
  onTaskUpdate
}) => {
  // Estado inicial vazio
  const initialState = {
    title: '',
    isGroupTitle: false,
    status: false,
    agent: { id: null },
    description: '',
    days_to_complete: 0
  };

  const [body, setBody] = useState(initialState);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Este useEffect é acionado quando o modal abre ou fecha
  useEffect(() => {
    if (show && data?.id) {
      // Quando o modal abre com novos dados
      setCurrentTaskId(data.id);
      setBody({
        title: data.title || '',
        isGroupTitle: data.isGroupTitle || false,
        status: data.status || false,
        agent: { id: data?.agent?.id || null },
        description: data.description || '',
        days_to_complete: data.days_to_complete || 0
      });
    } else if (!show) {
      // Limpa o estado quando o modal fecha
      setBody(initialState);
      setCurrentTaskId(null);
      setIsLoading(false);
    }
  }, [show, data?.id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setBody(prevBody => ({
      ...prevBody,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!body.title.trim()) {
      return; // Não submete se o título estiver vazio
    }

    setIsLoading(true);

    try {
      // Encontra o agente completo baseado no ID selecionado
      const selectedAgent = body.agent?.id
        ? agents.find(agent => agent.id === parseInt(body.agent.id))
        : null;

      const formattedBody = {
        id: data.id,
        title: body.title.trim(),
        isGroupTitle: body.isGroupTitle,
        status: body.status,
        days_to_complete: parseInt(body.days_to_complete) || 0,
        description: body.description?.trim() || '',
        agent: selectedAgent,
        position: data.position // Mantém a posição original
      };

      // Chama a função de callback para atualizar o estado no componente pai
      if (onTaskUpdate) {
        onTaskUpdate(formattedBody);
      }

      // Fecha o modal após salvar
      handleClose();
    } catch (error) {
      console.error('Erro ao editar tarefa:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      // Usar o ID da tarefa atual como "key" para forçar renderização completa
      key={`task-modal-${currentTaskId}`}
      onKeyDown={handleKeyDown}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {body.isGroupTitle ? 'Editar título do grupo' : 'Editar tarefa'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3" controlId="title">
            <Form.Label>Título <span className='text-danger'> *</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Digite o título da tarefa"
              name="title"
              value={body.title}
              onChange={handleChange}
              autoFocus
              required
            />
          </Form.Group>

          {!body.isGroupTitle && (
            <>
              <Row className="mb-3">
                <Form.Group as={Col} md={6} controlId="agent">
                  <Form.Label>Responsável</Form.Label>
                  <Form.Select
                    value={body?.agent?.id || ''}
                    onChange={e =>
                      setBody({
                        ...body,
                        agent: {
                          id: e.target.value ? parseInt(e.target.value) : null
                        }
                      })
                    }
                  >
                    <option value="">Sem responsável</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.firstName} {agent.lastName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group as={Col} md={6} controlId="days_to_complete">
                  <Form.Label>Duração (dias)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="0"
                    name="days_to_complete"
                    value={body.days_to_complete}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Row>

              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Digite uma descrição para a tarefa"
                  name="description"
                  value={body.description || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="status">
                <Form.Check
                  type="switch"
                  label="Tarefa concluída"
                  name="status"
                  checked={body.status || false}
                  onChange={() => setBody({ ...body, status: !body.status })}
                />
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3" controlId="isGroupTitle">
            <Form.Check
              type="switch"
              label="É um título de grupo"
              name="isGroupTitle"
              checked={body.isGroupTitle}
              onChange={() =>
                setBody({ ...body, isGroupTitle: !body.isGroupTitle })
              }
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || !body.title.trim()}
        >
          <FontAwesomeIcon icon={faSave} className="me-2" />
          {isLoading ? 'Salvando...' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TemplateTaskEditModal;
